/**
 * The painter's submission as every reviewer reads it (owner, 25 Oct), on the
 * web and on the phone: one contract, so the same fact is never shown two
 * ways. Read from GET /v1/quality/reviews/:id/submission, .../photos and
 * .../gps.
 */
import type { TranslateVars } from '../i18n/index';
import { unitLabel } from './unit-label';

export interface Submission {
  readonly reviewId: string;
  readonly jobRequestId: string;
  /** The vendor's assignment the job's quantity is recorded against. */
  readonly vendorAssignmentId: string;
  readonly reviewStatus: string;
  /** The decision this reader holds now; null when it is not his. */
  readonly myDecision: { readonly instanceId: string; readonly decidesOn: 'REVIEW' | 'WORKFLOW' } | null;
  readonly start: {
    readonly startedAt: string;
    readonly latitude: number | null;
    readonly longitude: number | null;
    readonly accuracyM: number | null;
    readonly distanceFromSiteM: number | null;
    readonly gpsClassification: string | null;
    readonly deviceId: string | null;
    readonly appVersion: string | null;
  } | null;
  readonly progress: readonly { readonly reportedAt: string; readonly pctComplete: number; readonly remarks: string | null; readonly deviceId: string | null }[];
  readonly area: {
    readonly approvedArea: number | null;
    readonly approvedUom: string | null;
    readonly actualArea: number | null;
    readonly actualUom: string | null;
    readonly variancePct: number | null;
    readonly justificationCode: string | null;
    readonly justificationRemarks: string | null;
    readonly capturedAt: string;
  } | null;
  readonly materials: readonly { readonly materialCode: string; readonly quantity: number; readonly uomCode: string | null; readonly remarks: string | null; readonly capturedAt: string }[];
  readonly issues: readonly {
    readonly reasonCode: string;
    readonly remarks: string | null;
    readonly reportedAt: string;
    readonly status: string;
    readonly vendorResponse: string | null;
    readonly vendorRespondedAt: string | null;
  }[];
  readonly completion: {
    readonly submittedAt: string | null;
    readonly status: string;
    readonly remarks: string | null;
    readonly durationMin: number | null;
    readonly totalPhotos: number | null;
    readonly beforePhotos: number | null;
    readonly duringPhotos: number | null;
    readonly afterPhotos: number | null;
    readonly locationPhotos: number | null;
    readonly dealerCertificatePhotos: number | null;
    readonly openIssues: number | null;
    readonly deviceId: string | null;
    readonly appVersion: string | null;
  } | null;
}

export interface SubmissionPhoto {
  readonly photoId: string;
  /** BEFORE, DURING, AFTER, LOCATION_CONTEXT or DEALER_CERTIFICATE. */
  readonly photoType: string;
  readonly capturedAt: string;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly accuracyM: number | null;
  readonly deviceId: string | null;
  readonly appVersion: string | null;
  /** Null until a decision is recorded on this photo. */
  readonly photoValidationId: string | null;
  readonly decision: string | null;
  readonly decisionAt: string | null;
  readonly rejectReasonCode: string | null;
}

export interface SubmissionGps {
  readonly gpsId: string;
  /** The moment of work the fix was taken at, e.g. START. */
  readonly source: string;
  readonly capturedAt: string;
  readonly execLatitude: number;
  readonly execLongitude: number;
  readonly accuracyM: number;
  readonly isMockProvider: boolean;
  /** Null when the job carries no site location. */
  readonly siteLatitude: number | null;
  readonly siteLongitude: number | null;
  readonly distanceM: number | null;
  readonly radiusM: number;
  readonly accuracyThresholdM: number;
  readonly withinRadius: boolean | null;
  readonly accuracyAcceptable: boolean;
  /** Null until a decision is recorded on this reading. */
  readonly gpsValidationId: string | null;
  readonly decision: string | null;
  readonly decisionAt: string | null;
  readonly overrideReason: string | null;
}

/** The job's dealer, site and work as M11 records it (GET /v1/closure/job-record/:job). */
export type JobRecord = Record<string, string | number | null>;

/** A completion review (GET /v1/quality/reviews/:id). It has no number of its own; it is known by its job. */
export interface ReviewDetail {
  readonly reviewId: string;
  readonly jobRequestId: string;
  readonly jobNumber: string | null;
  readonly vendorName: string | null;
  readonly vendorCode: string | null;
  readonly painterName: string | null;
  readonly painterCode: string | null;
  readonly reviewStatus: string;
  readonly cycleNo: number;
  readonly slaDeadlineAt: string;
  readonly startedAt: string | null;
  readonly decidedAt: string | null;
  readonly overallRemarks: string | null;
  readonly rejectionReasonCode: string | null;
}

/** The vendor's scorecard (GET .../score); null until he scores. */
export interface ScoreRecord {
  readonly qualityRating: number;
  readonly photoRating: number;
  readonly gpsRating: number;
  readonly areaRating: number;
  readonly overallScore?: number | null;
  readonly passThreshold?: number | null;
  readonly passed?: boolean | null;
}

/**
 * Whether the reader works the vendor's own review tools (score, decide,
 * return for rework): while his level is the reader's, or - for a job that runs
 * on no workflow - while the review is still open with him. A later reviewer
 * decides his level on the job's workflow instead (myDecision WORKFLOW).
 */
export function vendorToolsOpen(review: ReviewDetail, submission: Submission): boolean {
  const decidesOn = submission.myDecision?.decidesOn ?? null;
  return decidesOn === 'REVIEW'
    || (submission.myDecision === null
      && (review.reviewStatus === 'PENDING' || review.reviewStatus === 'IN_REVIEW'));
}

export const reviewPath = (reviewId: string, seg?: string): string =>
  `/v1/quality/reviews/${encodeURIComponent(reviewId)}${seg ? `/${seg}` : ''}`;

export const jobRecordPath = (jobRequestId: string): string =>
  `/v1/closure/job-record/${encodeURIComponent(jobRequestId)}`;

/** The painter's photo is served by M09 behind a signed link. */
export const photoLinkPath = (photoId: string): string =>
  `/v1/photo/${encodeURIComponent(photoId)}`;

/**
 * The path and query of a signed link, to fetch it through the app's own API
 * client with the login like every other API file. Read without `URL`, which
 * the phone's runtime does not fully implement.
 */
export function signedLinkPath(signedUrl: string): string {
  return signedUrl.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, '') || '/';
}

/** A latitude and longitude as every review screen writes them; a dash when either is missing. */
export function sitePosition(
  lat: number | string | null | undefined,
  lng: number | string | null | undefined,
): string {
  return lat === null || lat === undefined || lng === null || lng === undefined || lat === '' || lng === ''
    ? '\u2014'
    : `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
}

/** A position with the accuracy of its fix, e.g. "24.609929, 73.690317 ±60.23 m". */
export function fixText(
  lat: number | null,
  lng: number | null,
  accuracyM: number | null,
  decimal: (v: number) => string,
): string {
  return `${sitePosition(lat, lng)}${accuracyM !== null ? ` \u00b1${decimal(accuracyM)} m` : ''}`;
}

/** A photo or a GPS reading, named by what it is and when it was taken. */
export function capturedLabel(kind: string, capturedAt: string, at: (iso: string) => string): string {
  return [kind, at(capturedAt)].join(' \u00b7 ');
}

/** Metres in a GPS reading's columns, to one decimal; a dash when unknown. */
export function metresText(m: number | null): string {
  return m === null ? '\u2014' : m.toFixed(1);
}

/** Whether a reading sits inside the site radius with an acceptable fix and no mock provider. */
export function gpsReadingFine(g: SubmissionGps): boolean {
  return g.withinRadius === true && g.accuracyAcceptable && !g.isMockProvider;
}

type Translate = (key: string, vars?: TranslateVars) => string;

/** What the system's check says of a reading, in the reader's language. */
export function gpsReadingNotes(g: SubmissionGps, t: Translate): string[] {
  const notes: string[] = [];
  if (g.withinRadius === null) notes.push(t('quality.gps.noSite'));
  else notes.push(t(g.withinRadius ? 'quality.gps.inside' : 'quality.gps.outside', { radius: g.radiusM }));
  notes.push(t(g.accuracyAcceptable ? 'quality.gps.accuracyOk' : 'quality.gps.accuracyPoor', { limit: g.accuracyThresholdM }));
  if (g.isMockProvider) notes.push(t('quality.gps.mock'));
  return notes;
}

/** The tenant's own formats, as useBusinessDay / useBusinessNumber give them. */
export interface ReviewFormats {
  readonly t: Translate;
  readonly at: (iso: string) => string;
  readonly day: (iso: string) => string;
  readonly decimal: (v: number | string) => string;
  readonly money: (v: number) => string;
}

const DASH = '\u2014';
type Row = readonly [label: string, value: string];

/** The job, its dealer and site, label by label. */
export function jobRecordRows(rec: JobRecord, f: Pick<ReviewFormats, 't' | 'day' | 'decimal' | 'money'>): Row[] {
  const { t } = f;
  const v = (k: string): string => {
    const x = rec[k];
    return x === null || x === undefined || x === '' ? DASH : String(x);
  };
  const unit = unitLabel(String(rec.uom_code ?? ''));
  const area = (k: string): string => {
    const x = rec[k];
    return x === null || x === undefined || x === '' ? DASH : `${f.decimal(x)} ${unit}`.trim();
  };
  const joined = (parts: string[], sep: string): string => parts.filter((x) => x !== DASH).join(sep) || DASH;
  return [
    [t('quality.job.dealer'), rec.dealer_code ? `${v('dealer_firm_name')} (${v('dealer_code')})` : v('dealer_firm_name')],
    [t('quality.job.address'), joined([v('dealer_address'), v('dealer_city'), v('dealer_pincode')], ', ')],
    [t('quality.job.gstin'), v('dealer_gstin')],
    [t('quality.job.site'), sitePosition(rec.site_latitude, rec.site_longitude)],
    [t('quality.job.requestDate'), rec.request_date ? f.day(String(rec.request_date)) : DASH],
    [t('quality.job.workType'), v('work_type_name')],
    [t('quality.job.branding'), joined([v('branding_type_name'), v('branding_item_name')], ' / ')],
    [t('quality.job.workOrder'), v('assignment_code') !== DASH ? v('assignment_code') : v('work_order_number')],
    [t('quality.job.rate'), rec.rate_value === null || rec.rate_value === undefined ? DASH : `${f.money(Number(rec.rate_value))} / ${unit}`],
    [t('quality.job.areaRequested'), area('area_requested')],
    [t('quality.job.areaAllocated'), area('area_allocated')],
    [t('quality.job.areaExecuted'), area('area_executed')],
    [t('quality.job.salesOfficer'), v('sales_officer_name')],
    [t('quality.job.brandingOfficer'), v('branding_officer_name')],
    [t('quality.job.vendor'), rec.vendor_code ? `${v('vendor_name')} (${v('vendor_code')})` : v('vendor_name')],
    [t('quality.job.fieldForce'), v('field_force_name')],
    [t('quality.job.status'), v('current_status')],
  ];
}

/** The field work - start, hand-in, time, photos, issues - label by label. */
export function fieldWorkRows(s: Submission, f: Pick<ReviewFormats, 't' | 'at' | 'decimal'>): Row[] {
  const { t } = f;
  const { start, completion } = s;
  return [
    [t('quality.work.started'), start ? f.at(start.startedAt) : DASH],
    [t('quality.work.startGps'), start ? fixText(start.latitude, start.longitude, start.accuracyM, f.decimal) : DASH],
    [t('quality.work.distance'), `${start?.distanceFromSiteM !== null && start?.distanceFromSiteM !== undefined ? `${f.decimal(start.distanceFromSiteM)} m` : DASH}${start?.gpsClassification ? ` \u00b7 ${start.gpsClassification}` : ''}`],
    [t('quality.work.submitted'), completion?.submittedAt ? f.at(completion.submittedAt) : DASH],
    [t('quality.work.duration'), completion?.durationMin !== null && completion?.durationMin !== undefined ? t('quality.work.minutes', { n: completion.durationMin }) : DASH],
    [t('quality.work.photos'), completion
      ? t('quality.work.photoCounts', {
          total: completion.totalPhotos ?? 0,
          before: completion.beforePhotos ?? 0,
          during: completion.duringPhotos ?? 0,
          after: completion.afterPhotos ?? 0,
          location: completion.locationPhotos ?? 0,
          certificate: completion.dealerCertificatePhotos ?? 0,
        })
      : DASH],
    [t('quality.work.openIssues'), completion?.openIssues === null || completion?.openIssues === undefined ? DASH : String(completion.openIssues)],
    [t('quality.work.remarks'), completion?.remarks ?? DASH],
    [t('quality.work.device'), [completion?.deviceId ?? start?.deviceId, completion?.appVersion ?? start?.appVersion].filter(Boolean).join(' \u00b7 ') || DASH],
  ];
}

/** The area as the field force measured it, label by label; empty when none was captured. */
export function areaCapturedRows(
  s: Submission,
  f: Pick<ReviewFormats, 't' | 'at' | 'decimal'> & { readonly number: (v: number) => string },
): Row[] {
  const { t } = f;
  const a = s.area;
  if (!a) return [];
  return [
    [t('quality.area.approved'), a.approvedArea === null ? DASH : `${f.decimal(a.approvedArea)} ${unitLabel(a.approvedUom ?? '')}`],
    [t('quality.area.measured'), a.actualArea === null ? DASH : `${f.decimal(a.actualArea)} ${unitLabel(a.actualUom ?? '')}`],
    [t('quality.area.col.variance'), a.variancePct === null ? DASH : `${f.number(a.variancePct)}%`],
    [t('quality.area.justification'), [a.justificationCode, a.justificationRemarks].filter(Boolean).join(' \u2014 ') || DASH],
    [t('quality.photo.col.at'), f.at(a.capturedAt)],
  ];
}
